// Mocked reference-image data for the ideation Search tab. The four images are
// downscaled copies of the Figma design assets; they are cycled across every
// category until the real search backend is wired up.
import aircraft from 'images/three_d_studio/ideation/aircraft.jpg'
import animals from 'images/three_d_studio/ideation/animals.jpg'
import car from 'images/three_d_studio/ideation/car.jpg'
import character from 'images/three_d_studio/ideation/character.jpg'
import generated1 from 'images/three_d_studio/ideation/generated-1.jpg'

const IMAGE_POOL = [aircraft, animals, car, character]

const CATEGORY_DEFS = [
  { value: 'aircraft', name: 'Aircraft', cover: aircraft },
  { value: 'animals', name: 'Animals', cover: animals },
  { value: 'architectural', name: 'Architectural', cover: character },
  { value: 'exterior', name: 'Exterior', cover: car },
  { value: 'interior', name: 'Interior', cover: aircraft },
  { value: 'car', name: 'Car', cover: car },
  { value: 'character', name: 'Character', cover: character },
  { value: 'military', name: 'Military', cover: aircraft },
  { value: 'furniture', name: 'Furniture', cover: animals },
  { value: 'industrial', name: 'Industrial', cover: car },
  { value: 'art-design', name: 'Art & Design', cover: character },
  { value: 'home-living', name: 'Home & Living', cover: animals },
]

const buildImages = (categoryIndex, name) =>
  Array.from({ length: 9 }, (_, i) => ({
    id: (categoryIndex + 1) * 100 + i + 1,
    url: IMAGE_POOL[(categoryIndex + i) % IMAGE_POOL.length],
    alt: `${name} reference ${i + 1}`,
  }))

export const SEARCH_CATEGORIES = CATEGORY_DEFS.map((category, index) => ({
  ...category,
  images: buildImages(index, category.name),
}))

// The first four results returned for any successful mock search.
const SEARCH_RESULTS = IMAGE_POOL.map((url, index) => ({
  id: 9000 + index,
  url,
  alt: `Search result ${index + 1}`,
}))

const SEARCH_DELAY_MS = 2000

/**
 * Mocked image search until the real backend is wired up. Resolves after a
 * short delay: "car" returns no results; any other term returns the first four.
 * @param {string} term - The search term
 * @returns {Promise<Array<{ id: number, url: string, alt: string }>>}
 */
export const mockSearchImages = term => new Promise((resolve) => {
  setTimeout(() => {
    resolve(term.trim().toLowerCase() === 'car' ? [] : SEARCH_RESULTS)
  }, SEARCH_DELAY_MS)
})

// Quick-action tags shown in the Create composer; the set depends on how many
// reference images have been attached.
export const CREATE_TAGS = {
  SINGLE: ['Generate variation', 'Reuse style', 'Reuse character', 'Create front, side, and back views'],
  MULTI: ['Generate variation', 'Combine styles', 'Combine elements'],
}

// Images cycled as mocked generation outputs (the first is the Figma result render).
const GENERATED_POOL = [generated1, car, aircraft, character]
let generationCount = 0

/**
 * Mocked image generation until the real backend is wired up. Resolves after a
 * short delay with an outcome: 1 in every 4 attempts fails (~25%), starting with
 * the first so a send still surfaces the failed state for QA; the rest succeed,
 * cycling through GENERATED_POOL so repeated generations return different images.
 * @returns {Promise<{ failed: boolean, image?: { id: number, url: string, alt: string } }>}
 */
export const mockGenerateImage = () => new Promise((resolve) => {
  setTimeout(() => {
    generationCount += 1
    if (generationCount % 4 === 1) {
      resolve({ failed: true })
      return
    }
    const url = GENERATED_POOL[generationCount % GENERATED_POOL.length]
    resolve({ failed: false, image: { id: 20000 + generationCount, url, alt: 'Generated image' } })
  }, SEARCH_DELAY_MS)
})
